<template>
  <div class="bg-white shadow-lg rounded-lg overflow-hidden">
    <div class="bg-primary text-white p-4 flex justify-between items-center">
      <h3 class="text-xl font-semibold">User Management</h3>
      <button
        @click="goBack"
        class="bg-white text-black px-4 py-2 rounded-md hover:bg-gray-200 transition duration-300"
      >
        Back
      </button>
    </div>

    <div class="p-6 space-y-4">
      <div class="flex justify-between items-center">
        <h4 class="text-lg font-semibold text-primary">Admin Users</h4>
        <div class="flex justify-between mb-4">
          <button
            @click="openAddAdminModal"
            class="bg-primary text-white px-4 py-2 rounded-md hover:bg-purple-900 transition duration-300"
          >
            Add New Admin
          </button>
          <button
            @click="openExcelModal"
            class="ml-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-300"
          >
            Upload/Download Users
          </button>
        </div>
      </div>

      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Name
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Email
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Role
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Manages
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="admin in filteredAdmins" :key="admin.id">
            <td class="px-6 py-4 whitespace-nowrap">{{ admin.name }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ admin.email }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ admin.role }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ admin.manages }}</td>
            <td class="px-6 py-4 whitespace-nowrap">
              <button
                @click="editAdmin(admin)"
                class="text-indigo-600 hover:text-indigo-900 mr-2"
              >
                Edit
              </button>
              <button
                @click="removeAdmin(admin.id)"
                class="text-red-600 hover:text-red-900"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="mt-4 flex justify-between items-center">
        <div>
          <span class="text-sm text-gray-700">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems }} entries
          </span>
        </div>
        <div class="flex space-x-2">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-3 py-1 border rounded-md text-sm"
            :class="currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'"
          >
            Previous
          </button>
          <button
            v-for="page in totalPages"
            :key="page"
            @click="changePage(page)"
            class="px-3 py-1 border rounded-md text-sm"
            :class="currentPage === page ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
          >
            {{ page }}
          </button>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 border rounded-md text-sm"
            :class="currentPage === totalPages ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'"
          >
            Next
          </button>
        </div>
      </div>
      <div v-if="totalItems > itemsPerPage" class="mt-4">
        <ul class="flex justify-center">
          <li v-for="page in totalPages" :key="page" class="mx-2">
            <button @click="changePage(page)" class="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">
              {{ page }}
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Admin Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center"
    >
      <div class="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <h2 class="text-2xl font-bold mb-4">
          {{ isEditing ? "Edit Admin" : "Add New Admin" }}
        </h2>
        <form @submit.prevent="submitAdminForm">
          <div class="mb-4">
            <label for="name" class="block text-sm font-medium text-gray-700"
              >Name</label
            >
            <input
              type="text"
              id="name"
              v-model="currentAdmin.name"
              required
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
          <div class="mb-4">
            <label for="email" class="block text-sm font-medium text-gray-700"
              >Email</label
            >
            <input
              type="email"
              id="email"
              v-model="currentAdmin.email"
              required
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
          <div class="mb-4">
            <label for="role" class="block text-sm font-medium text-gray-700"
              >Role</label
            >
            <select
              id="role"
              v-model="currentAdmin.role"
              required
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            >
              <option value="admin" v-if="userRole==='admin'">Admin</option>
              <option value="school_admin">School Admin</option>
              <option value="department_admin">Department Admin</option>
              <option value="supervisor" v-if="userRole==='admin'">Supervisor</option>

            </select>
          </div>
          <div class="mb-4">
            <label for="manages" class="block text-sm font-medium text-gray-700"
              >Manages</label
            >
            <select
              id="manages"
              v-model="currentAdmin.manages"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            >
              <option value="">Select a department</option>
              <option
                v-for="dept in availableDepartments"
                :key="dept"
                :value="dept"
              >
                {{ dept }}
              </option>
            </select>
          </div>
          <div class="flex justify-end space-x-2">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              {{ isEditing ? "Update" : "Add" }} Admin
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Excel Modal -->
    <ExcelModal
      v-if="showExcelModal"
      :show="showExcelModal"
      :data-type="'Users'"
      @close="closeExcelModal"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import ExcelModal from "./modals/ExcelModal.vue";

export default {
  name: "UserManagement",
  components: {
    ExcelModal,
  },
  data() {
    return {
      showModal: false,
      showExcelModal: false,
      isEditing: false,
      currentAdmin: {
        id: "",
        name: "",
        email: "",
        role: "admin",
        manages: "",
      },
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    ...mapState("users", ["admins"]),
    ...mapState(["schools", "departments"]),
    ...mapState({
      userRole: state => state.auth.userRole,
      userDepartment: state => state.auth.userDepartment,
      userSchool: state => state.auth.userSchool,
    }),
    availableDepartments() {
      if (this.currentAdmin.role === "school_admin") {
       return this.schools.map(d=>d.fullName);
      }
      else if (this.currentAdmin.role == "department_admin")
        return this.departments.map((d) => d.fullName);
      else
        return []
    },
    filteredAdmins(){
      let admins = this.permissionAdmin;
      let size = 10;
      const start = (this.currentPage - 1) * size;
      const end = start + size;
      return admins.slice(start, end);
    },
    totalItems() {
      return this.permissionAdmin.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    permissionDepartments(){
      if (this.userRole === "school_admin") {
        return this.departments.filter((dept) => dept.school === this.userSchool);
      } else if (this.userRole === "department_admin") {
        return this.departments.filter((dept) => dept.fullName === this.userDepartment);
      } else {
        return this.departments;
      }
    },
    permissionAdmin(){
      let admins = this.admins;
      if (this.userRole === "school_admin") {
        let _schoolShortCode = this.schools.find((school) => school.fullName === this.userSchool).shortName;
        let departmentsUnderSchool = this.departments.filter((dept) => dept.school === _schoolShortCode).map(o=>o.fullName);  
        console.log(departmentsUnderSchool)
        admins = admins.filter((admin) => admin.manages === this.userSchool || departmentsUnderSchool.includes(admin.manages));
      } else if (this.userRole === "department_admin") {
        admins = admins.filter((admin) => admin.manages === this.userDepartment);
      }
      return admins
    }
  },
  created() {
    this.fetchAdmins();
    this.$store.dispatch("fetchSchools");
    this.$store.dispatch("fetchDepartments");
  },
  methods: {
    ...mapActions("users", [
      "fetchAdmins",
      "addAdmin",
      "updateAdmin",
      "removeAdmin",
    ]),
    goBack() {
      this.$router.push("/dashboard");
    },
    openAddAdminModal() {
      this.isEditing = false;
      this.currentAdmin = {
        id: "",
        name: "",
        email: "",
        role: "admin",
        manages: "",
      };
      this.showModal = true;
    },
    editAdmin(admin) {
      this.isEditing = true;
      this.currentAdmin = { ...admin };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.currentAdmin = {
        id: "",
        name: "",
        email: "",
        role: "admin",
        manages: "",
      };
    },
    async submitAdminForm() {
      try {
        if (this.isEditing) {
          if (this.currentAdmin.role == 'admin' || this.currentAdmin.role == 'supervisor')
            this.currentAdmin.manages = '';
          await this.updateAdmin(this.currentAdmin);
        } else {
          await this.addAdmin(this.currentAdmin);
        }
        this.closeModal();
        this.fetchAdmins();
      } catch (error) {
        console.error("Error submitting admin form:", error);
        // Display error message to the user
        alert("An error occurred while saving the admin. Please try again.");
      }
    },
    openExcelModal() {
      this.showExcelModal = true;
    },
    closeExcelModal() {
      this.showExcelModal = false;
    },
    changePage(page) {
      this.currentPage = page;
    },
  },
};
</script>

